import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useStoreValue } from 'store';
import { LayoutPage, LayoutContent, FormRenderer, Form, ExpensesFooter, WarningModal } from 'components';
import { ExpenseIncomeWarningModal } from 'components/form/ExpenseIncomeWarningModal';
import { initFormConfig, submitHandler, byCountry, resumeSavedExpenseDetails } from 'utils';
import { config } from '_config';
import { useEventTracking } from 'react-event-tracker';
import { getDataLayerElements } from 'utils/getDataLayerElements';
import { allowNextStep, calculatedTotalMonthlyExpenses } from 'utils/expensesFooterHelper';
import { useExpenseWarning } from 'hooks/useExpenseWarning';
import { getMonthlyIncome } from 'utils/getMonthlyIncome';
import { getExpenseIncomeWarning } from 'utils/getExpenseIncomeWarning';
import { isFeatureOn } from 'featureToggles';
import { useFormInitialValues } from '../../hooks';

const FORM_ID = 'expensesDetails';
const formConfig = initFormConfig(config.formConfigs[FORM_ID]);

export function ExpensesDetails() {
  const [storeState, updateStore] = useStoreValue();
  const history = useHistory();
  const { trackEvent } = useEventTracking();
  let initialValues = useFormInitialValues(FORM_ID);
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const [expenseIncomeWarning, setExpenseIncomeWarning] = useState(null);
  const onModalClose = useCallback(() => setWarningModalOpen(false), [setWarningModalOpen]);
  const { check, warnings } = useExpenseWarning();

  initialValues = resumeSavedExpenseDetails(initialValues);

  const nextButtonLabel = byCountry({
    AU: 'Next',
    NZ: 'Next',
  });

  const onSubmit = useCallback(
    ({ values, errors }) => {
      setExpenseIncomeWarning(null);
      setWarningModalOpen(false);

      const newAcquisition = {
        ...storeState.acquisition,
        expensesDetails: { ...values },
      };

      function updateStoreAndRedirect() {
        updateStore({
          activeStep: '/commitments',
          acquisition: newAcquisition,
        });

        history.push('/commitments');

        trackEvent({
          event: {
            category: 'application',
            action: 'application-navigation',
            location: 'expenses',
            label: nextButtonLabel,
          },
          ...getDataLayerElements({ ...storeState, acquisition: newAcquisition }),
        });
      }

      // The shared expense library owns field validation and the lower-expense inline notice.
      // Do not apply the native NZ hard stop for a zero calculated total on that path; it
      // otherwise prevents navigation without rendering any user-facing error.
      if (isFeatureOn('expensesLibrary') || allowNextStep(values)) {
        submitHandler({ submit: updateStoreAndRedirect, errors });
      }
    },
    [history, nextButtonLabel, storeState, trackEvent, updateStore],
  );

  const onClickNext = useCallback(
    ({ values, errors }) => {
      if (Object.keys(errors).length > 0) {
        return;
      }

      const incomeWarning = getExpenseIncomeWarning(
        getMonthlyIncome(storeState.acquisition.employmentDetails),
        calculatedTotalMonthlyExpenses(values),
      );

      if (incomeWarning) {
        setExpenseIncomeWarning(incomeWarning);
        setWarningModalOpen(true);
      } else if (!isFeatureOn('expensesLibrary') && !check(values, storeState)) {
        setExpenseIncomeWarning(null);
        setWarningModalOpen(true);
      } else {
        onSubmit({ values, errors });
      }
    },
    [check, onSubmit, storeState],
  );

  return (
    <LayoutPage noSessionTimeout>
      <Form
        id={FORM_ID}
        initialValues={initialValues}
        onSubmit={onClickNext}
        footer={isFeatureOn('expensesLibrary') ? null : <ExpensesFooter />}
        showSaveDraftButton
        saveDraftOnSubmit
        saveDraftOnSessionTimeout
      >
        {formData => {
          return (
            <LayoutContent>
              <FormRenderer config={formConfig} formData={formData} storeState={storeState} />
              <WarningModal
                title="Please review your living expenses input"
                isOpen={expenseIncomeWarning === null && isWarningModalOpen}
                onClose={onModalClose}
                onSubmit={() => onSubmit(formData.state)}
                warnings={warnings}
                editButtonText="Edit amount"
                confirmButtonText="Confirm and continue"
              />
              <ExpenseIncomeWarningModal
                isOpen={expenseIncomeWarning !== null && isWarningModalOpen}
                onClose={() => {
                  setExpenseIncomeWarning(null);
                  onModalClose();
                }}
                onSubmit={() => onSubmit(formData.state)}
                warning={expenseIncomeWarning}
                monthlyExpenses={calculatedTotalMonthlyExpenses(formData.state.values)}
                monthlyIncome={getMonthlyIncome(storeState.acquisition.employmentDetails)}
              />
            </LayoutContent>
          );
        }}
      </Form>
    </LayoutPage>
  );
}
